import { useCallback, useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Link, Navigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Save, X, Eye, EyeOff, Sparkles, ArrowRight, BookOpen, Package, Mail } from "lucide-react";
import EmailSequenceManager from "../components/EmailSequenceManager";
import { toast } from "sonner";
import SEOHead from "../components/SEOHead";

import { useTranslation } from 'react-i18next';
const EMPTY_PRODUCT = {
  name: "", tagline: "", description: "", price: 0, billing: "one-time",
  features: [], badge: "", download_filename: "", contact_only: false,
  is_subscription: false, stripe_price_id: "", hidden: false, grants_course_access: false,
};

const EMPTY_LESSON = {
  module_id: "", title: "", summary: "", order: 0,
  blocks: [{ type: "text", title: "Overview", content: "" }], published: true,
};

function ProductsPanel() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/products");
    setProducts(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...editing, features: editing.features };
      delete body.product_id;
      if (editing.product_id) {
        await api.put(`/admin/products/${editing.product_id}`, body);
      } else {
        await api.post("/admin/products", body);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${pid}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <SEOHead
        title={t('admin.admin_panel_max_employee')}
        description={t('admin.max_employee_admin_panel_user_management_system')}
        url="/admin"
        noIndex
      />
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// ADMIN · PRODUCTS</div>
          <h2 className="font-display text-3xl uppercase">{t('admin.product_catalog')}</h2>
        </div>
        <button
          data-testid="new-product-button"
          onClick={() => setEditing({ ...EMPTY_PRODUCT })}
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-5 py-3 font-bold uppercase tracking-wider text-xs"
        >
          <Plus className="w-4 h-4" /> New product
        </button>
      </div>

      <div className="border border-zinc-900 divide-y divide-zinc-900">
        {products.map((p) => (
          <div key={p.product_id} className="p-5 flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <div className="font-display text-lg uppercase">{p.name}</div>
                {p.hidden && <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 border border-zinc-800 px-2 py-0.5">{t('admin.hidden')}</span>}
                {p.is_subscription && <span className="font-mono text-[9px] uppercase tracking-wider text-amber-500 border border-amber-500/40 px-2 py-0.5">{t('admin.subscription')}</span>}
                {p.grants_course_access && <span className="font-mono text-[9px] uppercase tracking-wider text-green-400 border border-green-800 px-2 py-0.5">{t('admin.course_access')}</span>}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                {p.product_id} · ${Number(p.price).toFixed(2)} · {p.billing}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.product_id}`} className="border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-3 py-2 font-mono text-[10px] uppercase tracking-wider">
                <Edit2 className="w-3 h-3 inline mr-1.5" />Edit
              </button>
              <button onClick={() => remove(p.product_id)} data-testid={`delete-product-${p.product_id}`} className="border border-zinc-800 hover:border-red-500 hover:text-red-400 text-zinc-300 px-3 py-2 font-mono text-[10px] uppercase tracking-wider">
                <Trash2 className="w-3 h-3 inline mr-1.5" />Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.product_id ? "Edit product" : "New product"}>
          <div className="space-y-4">
            <Field label="NAME"><input className="admin-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="TAGLINE"><input className="admin-input" value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} /></Field>
            <Field label="DESCRIPTION"><textarea rows={3} className="admin-input" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t('admin.price_usd')}><input type="number" step="0.01" className="admin-input" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })} /></Field>
              <Field label="BILLING"><input className="admin-input" value={editing.billing} onChange={(e) => setEditing({ ...editing, billing: e.target.value })} placeholder="one-time / per month" /></Field>
            </div>
            <Field label={t('admin.features_one_per_line')}>
              <textarea rows={4} className="admin-input" value={(editing.features || []).join("\n")}
                onChange={(e) => setEditing({ ...editing, features: e.target.value.split("\n").filter(Boolean) })} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t('admin.badge_optional')}><input className="admin-input" value={editing.badge || ""} onChange={(e) => setEditing({ ...editing, badge: e.target.value })} /></Field>
              <Field label={t('admin.download_filename')}><input className="admin-input" value={editing.download_filename || ""} onChange={(e) => setEditing({ ...editing, download_filename: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t('admin.stripe_price_id_for_subscriptions')}>
                <input className="admin-input" value={editing.stripe_price_id || ""} onChange={(e) => setEditing({ ...editing, stripe_price_id: e.target.value })} placeholder="price_xxx" />
              </Field>
              <div className="flex flex-col gap-2 pt-5">
                <Toggle checked={editing.is_subscription} onChange={(v) => setEditing({ ...editing, is_subscription: v })}>{t('admin.recurring_subscription')}</Toggle>
                <Toggle checked={editing.contact_only} onChange={(v) => setEditing({ ...editing, contact_only: v })}>{t('admin.contact_sales_only')}</Toggle>
                <Toggle checked={editing.grants_course_access} onChange={(v) => setEditing({ ...editing, grants_course_access: v })}>{t('admin.unlocks_playbook')}</Toggle>
                <Toggle checked={editing.hidden} onChange={(v) => setEditing({ ...editing, hidden: v })}>{t('admin.hidden_from_catalog')}</Toggle>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-6 border-t border-zinc-900">
            <button onClick={() => setEditing(null)} className="border border-zinc-800 hover:border-zinc-500 text-zinc-300 px-5 py-3 font-mono text-[11px] uppercase tracking-wider">{t('admin.cancel')}</button>
            <button onClick={save} data-testid="save-product-button" className="bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs">
              <Save className="w-3.5 h-3.5 inline mr-2" />Save product
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CoursePanel() {
  const { t } = useTranslation();
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [editingModule, setEditingModule] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);

  const load = useCallback(async () => {
    const [m, l] = await Promise.all([
      api.get("/admin/course/modules"),
      api.get("/admin/course/lessons"),
    ]);
    setModules(m.data);
    setLessons(l.data);
    setSelectedModule((current) => current || (m.data.length ? m.data[0].module_id : current));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveModule = async () => {
    try {
      const body = { title: editingModule.title, subtitle: editingModule.subtitle || "", order: editingModule.order || 0 };
      if (editingModule.module_id) await api.put(`/admin/course/modules/${editingModule.module_id}`, body);
      else await api.post("/admin/course/modules", body);
      toast.success("Saved");
      setEditingModule(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const removeModule = async (mid) => {
    if (!window.confirm("Delete this module AND all its lessons?")) return;
    await api.delete(`/admin/course/modules/${mid}`);
    toast.success("Module deleted");
    load();
  };

  const saveLesson = async () => {
    try {
      const body = {
        module_id: editingLesson.module_id,
        title: editingLesson.title,
        summary: editingLesson.summary || "",
        order: editingLesson.order || 0,
        blocks: editingLesson.blocks || [],
        published: editingLesson.published !== false,
      };
      if (editingLesson.lesson_id) await api.put(`/admin/course/lessons/${editingLesson.lesson_id}`, body);
      else await api.post("/admin/course/lessons", body);
      toast.success("Lesson saved");
      setEditingLesson(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const removeLesson = async (lid) => {
    if (!window.confirm("Delete this lesson?")) return;
    await api.delete(`/admin/course/lessons/${lid}`);
    toast.success("Lesson deleted");
    load();
  };

  const updateBlock = (i, patch) => {
    const blocks = [...(editingLesson.blocks || [])];
    blocks[i] = { ...blocks[i], ...patch };
    setEditingLesson({ ...editingLesson, blocks });
  };
  const addBlock = (type) => setEditingLesson({
    ...editingLesson,
    blocks: [...(editingLesson.blocks || []), { type, title: "", content: "" }],
  });
  const removeBlock = (i) => {
    const blocks = [...(editingLesson.blocks || [])];
    blocks.splice(i, 1);
    setEditingLesson({ ...editingLesson, blocks });
  };
  const moveBlock = (i, dir) => {
    const blocks = [...(editingLesson.blocks || [])];
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    setEditingLesson({ ...editingLesson, blocks });
  };

  const moduleLessons = lessons.filter((l) => l.module_id === selectedModule).sort((a, b) => a.order - b.order);

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Modules list */}
      <aside className="col-span-12 md:col-span-4 border border-zinc-900 p-4 h-fit">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">{t('admin.modules')}</div>
          <button
            data-testid="new-module-button"
            onClick={() => setEditingModule({ title: "", subtitle: "", order: modules.length })}
            className="border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-wider"
          >
            <Plus className="w-3 h-3 inline mr-1" />New
          </button>
        </div>
        <ul className="space-y-1">
          {modules.map((m) => {
            const active = m.module_id === selectedModule;
            const count = lessons.filter((l) => l.module_id === m.module_id).length;
            return (
              <li key={m.module_id}>
                <button
                  onClick={() => setSelectedModule(m.module_id)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm border-l-2 ${
                    active ? "border-amber-500 bg-amber-500/10 text-white" : "border-transparent hover:bg-zinc-950 text-zinc-300"
                  }`}
                >
                  <span className="truncate">{m.title}</span>
                  <span className="font-mono text-[10px] text-zinc-500 ml-2">{count}</span>
                </button>
                {active && (
                  <div className="flex gap-1 mt-1 mb-2 pl-3">
                    <button onClick={() => setEditingModule(m)} className="font-mono text-[10px] uppercase text-zinc-500 hover:text-amber-500">{t('admin.edit')}</button>
                    <span className="text-zinc-700">·</span>
                    <button onClick={() => removeModule(m.module_id)} className="font-mono text-[10px] uppercase text-zinc-500 hover:text-red-400">{t('admin.delete')}</button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Lessons in selected module */}
      <section className="col-span-12 md:col-span-8">
        {selectedModule ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">
                LESSONS · {modules.find((m) => m.module_id === selectedModule)?.title}
              </div>
              <button
                data-testid="new-lesson-button"
                onClick={() => setEditingLesson({ ...EMPTY_LESSON, module_id: selectedModule, order: moduleLessons.length })}
                className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-xs"
              >
                <Plus className="w-3.5 h-3.5" /> New lesson
              </button>
            </div>
            <div className="border border-zinc-900 divide-y divide-zinc-900">
              {moduleLessons.length === 0 && <div className="p-6 text-zinc-500 text-sm">{t('admin.no_lessons_yet')}</div>}
              {moduleLessons.map((l) => (
                <div key={l.lesson_id} className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-zinc-500">#{l.order + 1}</span>
                      <span className="font-display text-sm uppercase">{l.title}</span>
                      {!l.published && <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 border border-zinc-800 px-1.5 py-0.5">{t('admin.draft')}</span>}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-600 mt-1">
                      {(l.blocks || []).length} blocks
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditingLesson(l)} data-testid={`edit-lesson-${l.lesson_id}`} className="border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider">
                      <Edit2 className="w-3 h-3 inline mr-1" />Edit
                    </button>
                    <button onClick={() => removeLesson(l.lesson_id)} className="border border-zinc-800 hover:border-red-500 hover:text-red-400 text-zinc-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider">
                      <Trash2 className="w-3 h-3 inline mr-1" />Del
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-zinc-500 text-sm">{t('admin.select_module_left')}</div>
        )}
      </section>

      {/* Module editor modal */}
      {editingModule && (
        <Modal onClose={() => setEditingModule(null)} title={editingModule.module_id ? "Edit module" : "New module"}>
          <div className="space-y-4">
            <Field label="TITLE"><input className="admin-input" value={editingModule.title} onChange={(e) => setEditingModule({ ...editingModule, title: e.target.value })} /></Field>
            <Field label="SUBTITLE"><input className="admin-input" value={editingModule.subtitle || ""} onChange={(e) => setEditingModule({ ...editingModule, subtitle: e.target.value })} /></Field>
            <Field label="ORDER"><input type="number" className="admin-input" value={editingModule.order || 0} onChange={(e) => setEditingModule({ ...editingModule, order: parseInt(e.target.value) || 0 })} /></Field>
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-6 border-t border-zinc-900">
            <button onClick={() => setEditingModule(null)} className="border border-zinc-800 hover:border-zinc-500 text-zinc-300 px-5 py-3 font-mono text-[11px] uppercase tracking-wider">{t('admin.cancel')}</button>
            <button onClick={saveModule} className="bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs">
              <Save className="w-3.5 h-3.5 inline mr-2" />Save
            </button>
          </div>
        </Modal>
      )}

      {/* Lesson editor modal */}
      {editingLesson && (
        <Modal onClose={() => setEditingLesson(null)} title={editingLesson.lesson_id ? "Edit lesson" : "New lesson"} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-8">
                <Field label="TITLE"><input className="admin-input" value={editingLesson.title} onChange={(e) => setEditingLesson({ ...editingLesson, title: e.target.value })} /></Field>
              </div>
              <div className="col-span-2">
                <Field label="ORDER"><input type="number" className="admin-input" value={editingLesson.order || 0} onChange={(e) => setEditingLesson({ ...editingLesson, order: parseInt(e.target.value) || 0 })} /></Field>
              </div>
              <div className="col-span-2 pt-5">
                <Toggle checked={editingLesson.published !== false} onChange={(v) => setEditingLesson({ ...editingLesson, published: v })}>{t('admin.published')}</Toggle>
              </div>
            </div>
            <Field label={t('admin.summary_shown_at_top')}><textarea rows={2} className="admin-input" value={editingLesson.summary || ""} onChange={(e) => setEditingLesson({ ...editingLesson, summary: e.target.value })} /></Field>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">{t('admin.content_blocks')}</div>
                <div className="flex gap-2">
                  {["text", "prompt", "video", "pdf", "callout"].map((t) => (
                    <button key={t} onClick={() => addBlock(t)} className="border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider">
                      + {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                {(editingLesson.blocks || []).map((b, i) => (
                  <div key={i} className="border border-zinc-900 bg-zinc-950 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 border border-amber-500/30 px-2 py-0.5">{b.type}</span>
                        <input placeholder={t('admin.block_title_optional')} className="bg-transparent font-mono text-xs text-zinc-300 outline-none border-b border-transparent focus:border-zinc-700"
                          value={b.title || ""} onChange={(e) => updateBlock(i, { title: e.target.value })} />
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => moveBlock(i, -1)} className="font-mono text-[10px] text-zinc-500 hover:text-amber-500 px-1">↑</button>
                        <button onClick={() => moveBlock(i, 1)} className="font-mono text-[10px] text-zinc-500 hover:text-amber-500 px-1">↓</button>
                        <button onClick={() => removeBlock(i)} className="font-mono text-[10px] text-zinc-500 hover:text-red-400 px-1"><X className="w-3 h-3" /></button>
                      </div>
                    </div>
                    {b.type === "video" || b.type === "pdf" ? (
                      <input placeholder={b.type === "video" ? "YouTube/Vimeo URL" : "PDF URL"} className="admin-input" value={b.content} onChange={(e) => updateBlock(i, { content: e.target.value })} />
                    ) : (
                      <textarea rows={b.type === "text" ? 6 : 4} placeholder={b.type === "prompt" ? "The prompt students will copy..." : "Markdown content..."} className="admin-input font-mono text-[13px]" value={b.content} onChange={(e) => updateBlock(i, { content: e.target.value })} />
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-3 border-t border-zinc-900 flex items-center gap-3 text-xs">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-zinc-500">R$→USD helper:</span>
                <RBRConverter />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-6 border-t border-zinc-900">
            <button onClick={() => setEditingLesson(null)} className="border border-zinc-800 hover:border-zinc-500 text-zinc-300 px-5 py-3 font-mono text-[11px] uppercase tracking-wider">{t('admin.cancel')}</button>
            <button onClick={saveLesson} data-testid="save-lesson-button" className="bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-bold uppercase tracking-wider text-xs">
              <Save className="w-3.5 h-3.5 inline mr-2" />Save lesson
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RBRConverter() {
  const [brl, setBrl] = useState("");
  const usd = brl ? (parseFloat(brl) / 5).toFixed(2) : "";
  return (
    <div className="flex items-center gap-2">
      <span className="text-zinc-500">R$</span>
      <input type="number" value={brl} onChange={(e) => setBrl(e.target.value)} className="w-20 bg-black border border-zinc-800 px-2 py-1 font-mono text-xs focus:border-amber-500 focus:outline-none" />
      <span className="text-zinc-500">=</span>
      <span className="font-mono text-xs text-amber-500 min-w-[60px]">${usd || "0.00"}</span>
      <span className="text-zinc-600 text-[10px]">(÷5)</span>
    </div>
  );
}

function Modal({ onClose, title, children, wide }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-label={title}>
      <div className={`bg-[#09090B] border border-zinc-800 w-full ${wide ? "max-w-4xl" : "max-w-2xl"} my-8`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
          <div className="font-display text-xl uppercase">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white focus-visible:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded-sm min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label={t('admin.close_dialog')}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 block mb-2">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <div onClick={() => onChange(!checked)} className={`w-9 h-5 border flex items-center px-0.5 transition-colors ${checked ? "bg-amber-500 border-amber-500" : "bg-black border-zinc-700"}`}>
        <div className={`w-3.5 h-3.5 transition-transform ${checked ? "translate-x-[18px] bg-black" : "translate-x-0 bg-zinc-500"}`} />
      </div>
      <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-300">{children}</span>
    </label>
  );
}

export default function Admin() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-sm">{t('common.loading')}</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />
      <div className="max-w-xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-4xl uppercase mb-4">{t('admin.admin_only')}</h1>
        <p className="text-zinc-400 mb-8">{t('admin.your_account_doesnt_have_admin_privileges')}</p>
        <Link to="/dashboard" className="inline-flex items-center gap-2 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-zinc-300 px-5 py-3 font-mono text-xs uppercase tracking-wider">
          Go to dashboard <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// ADMIN CONSOLE</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-8">{t('admin.control_center')}</h1>

        <div className="border-b border-zinc-900 mb-8 flex gap-2">
          {[
            { id: "products", label: "Products", icon: Package },
            { id: "course", label: "Course", icon: BookOpen },
            { id: "email", label: "Email", icon: Mail },
          ].map((t) => {
            const I = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                  active ? "border-amber-500 text-amber-500" : "border-transparent text-zinc-400 hover:text-white"
                }`}
              >
                <I className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "products" ? <ProductsPanel /> : tab === "email" ? <EmailSequenceManager /> : <CoursePanel />}
      </div>
      <Footer />
    </div>
  );
}
